import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ConfigurationType, ConnectorTypes } from '@bciti/types';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CONNECTOR_TYPES_BY_TAG, ConfiguredConnector, Connector } from 'src/app/interfaces/config/config.interface';
import { ConfigurationService } from 'src/app/services/configuration/configuration.service';
import { ConfirmDeleteDialogComponent } from '../../dialogs/confirm-delete-dialog/confirm-delete-dialog.component';
import { AddEditConnectorDialogFormComponent } from './add-edit-connector-dialog-form/add-edit-connector-dialog-form.component';

@Component({
    selector: 'app-connector-form',
    templateUrl: './connector-form.component.html',
    styleUrls: ['./connector-form.component.scss'],
})
export class ConnectorFormComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject();

    @Input() orgTag: string;
    @Input() type: ConnectorTypes;
    @Input() connectors: Connector[];
    @Input() configuredConnectors: ConfiguredConnector[];
    @Output() reload = new EventEmitter();

    get matchedConnectors() {
        return this.connectors?.filter((c) => (CONNECTOR_TYPES_BY_TAG[c.tag] ?? [c.type]).includes(this.type)) ?? [];
    }

    get existingConnectors() {
        return this.configuredConnectors?.filter((cc) => cc.type === this.type) ?? [];
    }

    get selectedConnectorControl() {
        return this.selectConnectorForm.get('selectedConnector');
    }

    selectConnectorForm = new FormGroup({
        selectedConnector: new FormControl(undefined, [Validators.required]),
    });

    constructor(private dialog: MatDialog, private configurationService: ConfigurationService, private toastr: ToastrService) {}

    ngOnInit(): void {}

    openAddConnectorDialogForm() {
        if (this.selectConnectorForm.valid) {
            this.dialog
                .open(AddEditConnectorDialogFormComponent, {
                    autoFocus: false,
                    disableClose: true,
                    width: '600px',
                    data: {
                        connector: this.selectedConnectorControl.value,
                        configuredConnectors: this.configuredConnectors,
                        orgTag: this.orgTag,
                        type: this.type,
                    },
                })
                .afterClosed()
                .subscribe((response) => {
                    if (response) {
                        this.reload.emit(true);
                    }
                });
        }
    }

    openEditConnectorDialogForm(configuredCconnector: any) {
        if (configuredCconnector) {
            this.dialog
                .open(AddEditConnectorDialogFormComponent, {
                    autoFocus: false,
                    disableClose: true,
                    hasBackdrop: true,
                    width: '600px',
                    data: {
                        connector: this.matchedConnectors.find((c) => c.tag === configuredCconnector.tag),
                        existingConnector: configuredCconnector,
                        configuredConnectors: this.configuredConnectors,
                        orgTag: this.orgTag,
                        type: this.type,
                    },
                })
                .afterClosed()
                .subscribe((response) => {
                    if (response) {
                        this.reload.emit(true);
                    }
                });
        }
    }

    openConfirmDeleteConfiguredConnectorDialog(configuredConnector: ConfiguredConnector) {
        const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
            autoFocus: false,
            disableClose: true,
            hasBackdrop: true,
            width: '600px',
            data: {
                title: `Deleting ${configuredConnector.tag}`,
                body: ['Are you sure you want to delete this configured-connector?'],
                loading: false,
            },
        });

        const confirmDeleteSub = dialogRef.componentInstance.confirm.pipe(takeUntil(this.destroy$)).subscribe((v) => {
            if (v) {
                const payload = {
                    configuration: {
                        key: ConfigurationType.CONFIGUREDCONNECTORS,
                        data: {
                            // Keyed on (tag, type): the same connector can be configured under
                            // several types, and deleting one must leave the others alone.
                            connectors: this.configuredConnectors.filter(
                                (cc) => !(cc.tag === configuredConnector.tag && cc.type === configuredConnector.type),
                            ),
                        },
                    },
                };

                dialogRef.componentInstance.data.loading = true;
                this.configurationService
                    .patchConfigurations(this.orgTag, payload)
                    .pipe(takeUntil(this.destroy$))
                    .subscribe(
                        (response) => {
                            dialogRef.close(true);
                            this.toastr.success(`${configuredConnector.tag} configured-connector was deleted.`);
                        },
                        (error) => {
                            dialogRef.componentInstance.data.loading = false;
                            this.toastr.error(`An error occured while deleting ${configuredConnector.tag} configured-connector`);
                        },
                    );
            }
        });

        dialogRef
            .afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe((v) => {
                if (v) {
                    this.reload.emit(true);
                }
                confirmDeleteSub.unsubscribe();
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
